import * as React from "react";
import { Grid, FormControl, Divider, Checkbox, Alert } from "@mui/material";
import {
  Select,
  SelectUF,
  SelectCidade,
  TextField,
  CNPJField,
  CPFField,
  CEPField,
  FloatField,
  TelField,
  AsyncAutocomplete,
} from "./";
import { listaComoSoubePrograma } from "../static";
import { classificarPorte, origemDoPorte } from "../helpers/porte";
// import { regioes, listaComoSoubePrograma, operaFeriados } from "../static";
import api from "../services/api";
import { useStoreState, useStoreActions } from "easy-peasy";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const CamposProcessoInicial = ({
  useFormRef,
  schema,
  disabled,
  processo,
  showRegiao,
  isProcel,
}) => {
  let startLoading = useStoreActions((actions) => actions.nav.startLoading);
  let stopLoading = useStoreActions((actions) => actions.nav.stopLoading);
  let loading = useStoreState((state) => state.nav.loading);
  const [setoresAtuacao, setSetoresAtuacao] = React.useState(null);
  const [cnaes, setCnaes] = React.useState(null);
  const { watch, setValue, resetField, getFieldState } = useFormRef;

  const watchEstado = watch("estado");
  const watchSgtUnidadeId = watch("sgtUnidadeId");
  const watchSgtUnidadeNome = watch("sgtUnidadeNome");

  const fetchSgtUnidades = React.useCallback(
    async (search, page) => {
      const { data } = await api.http.get("/sgt/unidades", {
        params: { search: search || undefined, estado: watchEstado || undefined, page: page || undefined },
      });
      return data;
    },
    [watchEstado]
  );

  // Limpa a unidade só numa troca de estado feita de fato pela pessoa (evento do
  // SelectUF) - vigiar watch("estado") via useEffect não dá pra distinguir isso de
  // um reset() de carregamento de dados (o estado também "muda" de '' pro valor
  // salvo nesse momento, e limparia a unidade que acabou de ser carregada).
  const onEstadoAlteradoPelaPessoa = () => {
    if (watchSgtUnidadeId) {
      setValue("sgtUnidadeId", null, { shouldDirty: true });
      setValue("sgtUnidadeNome", null, { shouldDirty: true });
    }
  };

  const [states, setStates] = React.useState([]);
  //const [selectedState, setSelectedState] = React.useState('');
  const [cities, setCities] = React.useState([]);
  const [regioes, setRegioes] = React.useState([]);

  const watchCodigoPorteReceita = watch("codigoPorteReceita");
  const watchValorFaturamento = watch("valorFaturamento");

  // Porte só por faturamento: faixa da Receita Federal e, na falta dela, o
  // faturamento informado. O nº de funcionários não entra (ver helpers/porte).
  const dadosDoPorte = React.useMemo(
    () => ({
      codigoPorteReceita: watchCodigoPorteReceita,
      valorFaturamento: watchValorFaturamento,
    }),
    [watchCodigoPorteReceita, watchValorFaturamento]
  );

  React.useEffect(() => {
    setValue("porte", classificarPorte(dadosDoPorte) || "", { shouldValidate: true });
  }, [dadosDoPorte, setValue]);


  const cepField = watch("cep");

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const regimesfuncionamentos = [
    {
      id: "seg",
      name: "Segunda-Feira",
    },
    {
      id: "ter",
      name: "Terça-Feira",
    },
    {
      id: "qua",
      name: "Quarta-Feira",
    },
    {
      id: "qui",
      name: "Quinta-Feira",
    },
    {
      id: "sex",
      name: "Sexta-Feira",
    },
    {
      id: "sab",
      name: "Sábado",
    },
    {
      id: "dom",
      name: "Domingo",
    },
  ];



  const loadSetoresAtuacao = async (mount) => {
    startLoading();
    try {
      let data = (await api.http.get("/setores-atuacao")).data;
      if (mount.isMounted) setSetoresAtuacao(data);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: "error",
      });
      throw error;
    }
  };

  const loadCnae = async (mount) => {
    startLoading();
    try {
      let data = (await api.http.get("/cnae")).data;
      if (mount.isMounted) setCnaes(data);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: "error",
      });
      throw error;
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true,
    };
    loadSetoresAtuacao(mount);
    loadCnae(mount);
    return () => {
      mount.isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    ///recuperar dados da api ibge e preencher o dropdow estados
    async function fetchStates() {
      try {
        const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
        const data = await response.json();

        const sortedStates = data.sort((a, b) => a.nome.localeCompare(b.nome));

        setStates(sortedStates);
        ////  setStates(data);

      } catch (error) {
        console.error('Error fetching states:', error);
      }
    }

    fetchStates();
  }, []);

  React.useEffect(() => {
    async function fetchCities() {
      if (watchEstado) {
        try {
          const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${watchEstado}/municipios`);
          const data = await response.json();
          setCities(data);
        } catch (error) {
          console.error('Error fetching cities:', error);
        }
      } else {
        setCities([]);
      }
    }

    async function fetchRegioes() {
      startLoading();
      try {
        if (watchEstado) {
          let data = (await api.http.get(`/regioes/estado/${watchEstado}`)).data;
          setRegioes(data);
        }
        stopLoading();
      } catch (error) {
        stopLoading({
          message: error.toString(),
          severity: "error",
        });
        throw error;
      }
    }

    fetchCities();
    fetchRegioes();
  }, [watchEstado]);

  const formatCep = (value) => {
    let v = (value || "").replace(/\D/g, "");
    v = v.replace(/(\d{5})(\d)/, "$1-$2");
    v = v.replace(/(-\d{3})\d+?$/, "$1");
    return v;
  };

  const lastSearchedCepRef = React.useRef("");

  const loadCEP = async (rawCep, isMounted = true) => {
    const digits = (rawCep || "").replace(/\D/g, "");
    if (digits.length !== 8) return;
    if (lastSearchedCepRef.current === digits) return;
    lastSearchedCepRef.current = digits;

    startLoading();
    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      if (!response.ok) throw new Error(`ViaCEP respondeu HTTP ${response.status}`);
      const cepResp = await response.json();
      if (cepResp.erro) throw new Error("CEP não encontrado");
      if (isMounted) {
        if (cepResp.logradouro) setValue("rua", cepResp.logradouro, { shouldValidate: true });
        if (cepResp.bairro) setValue("bairro", cepResp.bairro, { shouldValidate: true });
        if (cepResp.localidade) setValue("cidade", cepResp.localidade, { shouldValidate: true });
        if (cepResp.uf) setValue("estado", cepResp.uf, { shouldValidate: true });
        setValue("cep", formatCep(digits), { shouldValidate: true });
      }
    } catch (error) {
      console.warn("Não foi possível consultar o CEP no ViaCEP:", error);
    } finally {
      stopLoading();
    }
  };

  const cnpjField = watch("cnpj");
  const [consultaCnpj, setConsultaCnpj] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;

    const consultarCnpj = async () => {
      const digits = (cnpjField || "").replace(/\D/g, "");
      // só roda para cadastro novo: evita sobrescrever dados de um processo já existente/revisado
      if (processo?.id || digits.length !== 14) {
        setConsultaCnpj(null);
        return;
      }

      try {
        const { data } = await api.http.get(`/consulta-cnpj/${digits}`);
        if (!mounted) return;
        setConsultaCnpj(data);

        if (data.brasilApi?.status === "found") {
          const empresa = data.brasilApi.data;
          setValue("razaoSocial", empresa.razaoSocial || "", { shouldValidate: true });
          setValue("nomeFantasia", empresa.nomeFantasia || "", { shouldValidate: true });
          setValue("classeCnae", empresa.cnaeCodigo || "", { shouldValidate: true });
          setValue("figuraCodigo", empresa.figuraCodigo || "", { shouldValidate: true });
          setValue("figuraDescricao", empresa.figuraDescricao || "", { shouldValidate: true });
          setValue("codigoPorteReceita", empresa.codigoPorte ?? null, { shouldValidate: true });
          setValue("optanteMei", empresa.optanteMei ?? null, { shouldValidate: true });

          const formattedCep = empresa.cep ? formatCep(empresa.cep) : "";
          if (formattedCep) {
            setValue("cep", formattedCep, { shouldValidate: true });
          }
          if (empresa.rua) setValue("rua", empresa.rua, { shouldValidate: true });
          if (empresa.numero) setValue("numero", empresa.numero, { shouldValidate: true });
          if (empresa.complemento) setValue("complemento", empresa.complemento, { shouldValidate: true });
          if (empresa.bairro) setValue("bairro", empresa.bairro, { shouldValidate: true });
          if (empresa.cidade) setValue("cidade", empresa.cidade, { shouldValidate: true });
          if (empresa.estado) setValue("estado", empresa.estado, { shouldValidate: true });

          // Se a API retornou o CEP e algum campo de endereço ainda não veio preenchido,
          // consulta o CEP para preencher rua, bairro, cidade e estado automaticamente
          // Não consultar o CEP automaticamente ao carregar dados do CNPJ/banco.
          // A consulta ocorre somente quando o usuário edita e sai do campo CEP.
        }

      } catch (error) {
        console.error("Erro ao consultar CNPJ na Receita Federal/SGT:", error);
      }
    };

    consultarCnpj();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpjField]);

  // Processo já cadastrado sem a classificação da Receita (a consulta acima só roda
  // em cadastro novo): busca só o código de porte e o MEI - nada de razão social ou
  // endereço, que alguém já pode ter revisado. Sem isso, o porte desse processo não
  // teria de onde sair além do faturamento informado.
  const receitaConsultadaRef = React.useRef(null);
  React.useEffect(() => {
    const digits = (cnpjField || "").replace(/\D/g, "");
    const jaTemCodigo =
      watchCodigoPorteReceita !== null &&
      watchCodigoPorteReceita !== undefined &&
      watchCodigoPorteReceita !== "";
    if (!processo?.id || digits.length !== 14 || jaTemCodigo) return;
    if (receitaConsultadaRef.current === processo.id) return;
    receitaConsultadaRef.current = processo.id;

    let mounted = true;
    api.http
      .get(`/consulta-cnpj/${digits}`)
      .then(({ data }) => {
        if (!mounted || data?.brasilApi?.status !== "found") return;
        const empresa = data.brasilApi.data;
        setValue("codigoPorteReceita", empresa.codigoPorte ?? null, { shouldDirty: true });
        setValue("optanteMei", empresa.optanteMei ?? null, { shouldDirty: true });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [processo?.id, cnpjField, watchCodigoPorteReceita, setValue]);

  return (
    <React.Fragment>
      <Divider>
        <h3>Dados da empresa</h3>
      </Divider>
      <FormControl sx={{ my: 2 }} fullWidth>
        <CNPJField
          field="cnpj"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl>
      {consultaCnpj?.brasilApi?.status === "not_found" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          CNPJ não encontrado na Receita Federal. Confira os dados preenchidos manualmente.
        </Alert>
      )}
      {consultaCnpj?.brasilApi?.status === "found" &&
        consultaCnpj.brasilApi.data.situacaoCadastral &&
        consultaCnpj.brasilApi.data.situacaoCadastral.toUpperCase() !== "ATIVA" && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Situação cadastral na Receita Federal: {consultaCnpj.brasilApi.data.situacaoCadastral}
          </Alert>
        )}
      {consultaCnpj?.sgt?.jaCadastrado && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Esta empresa já possui cadastro no SGT.
        </Alert>
      )}
      <FormControl sx={{ my: 2 }} fullWidth>
        <TextField
          field="razaoSocial"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl>
      <FormControl sx={{ my: 2 }} fullWidth>
        <TextField
          field="nomeFantasia"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl>
      <Grid container spacing={2} fullWidth justifyContent={"center"}>
        <Grid item xs={6}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <TextField
              field="numFuncs"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              type="text"
              disabled={disabled}
              useFormRef={useFormRef}
              schema={schema}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <FloatField
              field="valMedioFatEnergia"
              disabled={disabled}
              useFormRef={useFormRef}
              schema={schema}
            />
          </FormControl>
        </Grid>
      </Grid>

      <FormControl sx={{ my: 2 }} fullWidth>
        <FloatField
          field="valorFaturamento"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl>

      <FormControl sx={{ my: 2 }} fullWidth>
        {setoresAtuacao && (
          <Select
            field="setorAtuacaoId"
            useFormRef={useFormRef}
            schema={schema}
            options={setoresAtuacao}
            getOptionLabel={(option) => option.nome}
            disabled={disabled}
          />
        )}
      </FormControl>

      <FormControl sx={{ my: 2 }} fullWidth>
        {cnaes && (
          <Select
            field="cnaeId"
            useFormRef={useFormRef}
            schema={schema}
            options={cnaes}
            getOptionLabel={(option) => option.nome}
            disabled={disabled}
          />
        )}

      </FormControl>
      {/* <TextField
          field="cnaeId"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl> */}

      <FormControl sx={{ my: 2 }} fullWidth>
        <TextField
          field="classeCnae"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl>

      <FormControl sx={{ my: 2 }} fullWidth>
        <TextField
          field="porte"
          disabled={true}   // sempre desabilitado
          useFormRef={useFormRef}
          schema={schema}
          helperText={origemDoPorte(dadosDoPorte)}
        />
      </FormControl>

      <FormControl sx={{ my: 2 }} fullWidth>
        <TextField
          field="figuraDescricao"
          disabled={true}   // preenchido automaticamente via Receita Federal
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl>

      <FormControl sx={{ my: 2 }} fullWidth>
        <CEPField
          field="cep"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
          onBlur={() => loadCEP(cepField)}
        />
      </FormControl>

      <Grid container spacing={2}>
        <Grid item xs={8}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <TextField
              field="rua"
              disabled={disabled}
              useFormRef={useFormRef}
              schema={schema}
            />
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <TextField
              field="numero"
              disabled={disabled}
              useFormRef={useFormRef}
              schema={schema}
            />
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <TextField
              field="complemento"
              disabled={disabled}
              useFormRef={useFormRef}
              schema={schema}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <TextField
              field="bairro"
              disabled={disabled}
              useFormRef={useFormRef}
              schema={schema}
            />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <SelectUF
              field="estado"
              useFormRef={useFormRef}
              schema={schema}
              options={states}
              getOptionLabel={(option) => option.nome}
              onValueChange={onEstadoAlteradoPelaPessoa}
            />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <SelectCidade
              field="cidade"
              useFormRef={useFormRef}
              schema={schema}
              options={cities}
              getOptionLabel={(option) => option.nome}
            />
          </FormControl>
        </Grid>
      </Grid>

      {isProcel && (
        <FormControl sx={{ my: 2 }} fullWidth>
          <AsyncAutocomplete
            label="Estado/Unidade operacional (SGT)"
            value={
              watchSgtUnidadeId
                ? { id: watchSgtUnidadeId, abreviacao: watchSgtUnidadeNome }
                : null
            }
            fetchOptions={fetchSgtUnidades}
            getOptionLabel={(option) => option?.abreviacao || option?.descricao || ""}
            noOptionsText={`Nenhuma unidade operacional encontrada para o estado ${watchEstado || "selecionado"}`}
            disabled={disabled || !watchEstado}
            onChange={(unidade) => {
              setValue("sgtUnidadeId", unidade?.id ?? null, { shouldDirty: true });
              setValue("sgtUnidadeNome", unidade?.abreviacao ?? null, { shouldDirty: true });
            }}
          />
        </FormControl>
      )}

      {showRegiao && !isProcel && watchEstado?.toUpperCase() === "SP" && (
        <FormControl sx={{ my: 2 }} fullWidth>
          <Select
            field="regiaoId"
            useFormRef={useFormRef}
            schema={schema}
            options={regioes}
            disabled={disabled}
          />
        </FormControl>
      )}

      <Divider>
        <h3>Dados do responsável legal</h3>
      </Divider>

      <FormControl sx={{ my: 2 }} fullWidth>
        <TextField
          field="nome"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl>
      <FormControl sx={{ my: 2 }} fullWidth>
        <CPFField
          field="cpf"
          disabled={disabled}
          useFormRef={useFormRef}
          schema={schema}
        />
      </FormControl>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <TelField
              field="telefone"
              disabled={disabled}
              useFormRef={useFormRef}
              schema={schema}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <TextField
              field="email"
              type="email"
              disabled={disabled}
              useFormRef={useFormRef}
              schema={schema}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Divider>
        <h3>Outros</h3>
      </Divider>

      <FormControl sx={{ my: 2 }} fullWidth>
        <Select
          field="comoSoubeProgramaId"
          useFormRef={useFormRef}
          schema={schema}
          options={listaComoSoubePrograma}
          disabled={disabled}
        />
      </FormControl>
    </React.Fragment>
  );
};

export default CamposProcessoInicial;
